import { Component } from '@theme/component';

/**
 * The scroll and hover behaviour for `sections/site-header.liquid`.
 *
 * Three jobs:
 *  - drive `--sh-progress` (0 over the hero, 1 once scrolled past the fade distance), which the
 *    section's CSS uses to interpolate colour, height and the paper wash;
 *  - hide the bar on scroll-down and bring it back on scroll-up;
 *  - open the desktop menu panel on hover or keyboard focus.
 *
 * It also publishes `--header-height` / `--header-group-height` on the body. Horizon's inline
 * script in `layout/theme.liquid` looks for a `header-component` element and returns early when
 * it does not find one, so this section has to set them itself or every sticky offset in the
 * theme falls back to its default.
 *
 * @typedef {{ navLinks?: HTMLAnchorElement[], panels?: HTMLElement[], panelShell?: HTMLElement }} SiteHeaderRefs
 * @extends Component<SiteHeaderRefs>
 */
class SiteHeaderComponent extends Component {
  /** Scroll position at the previous sample, to get a direction from. */
  #lastY = 0;
  #frame = 0;
  /** Below this the bar always shows, so a short scroll never hides it. */
  #revealAbove = 120;
  /** Movement in px that counts as a deliberate direction change rather than a wobble. */
  #directionThreshold = 5;
  #openPanel = null;
  #desktop = window.matchMedia('(min-width: 750px)');

  connectedCallback() {
    super.connectedCallback();

    this.#lastY = window.scrollY;

    window.addEventListener('scroll', this.#onScroll, { passive: true });
    this.addEventListener('pointerenter', this.#onPointerEnter, true);
    this.addEventListener('focusin', this.#onFocusIn);
    this.addEventListener('pointerleave', this.#onLeave);
    this.addEventListener('focusout', this.#onLeave);
    this.addEventListener('keydown', this.#onKeydown);

    this.#desktop.addEventListener('change', this.#onBreakpoint);

    this.#resizeObserver = new ResizeObserver(this.#measure);
    this.#resizeObserver.observe(this);

    this.#measure();
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('scroll', this.#onScroll);
    this.#desktop.removeEventListener('change', this.#onBreakpoint);
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  get #overlay() {
    return this.dataset.overlay === 'true';
  }

  get #hideOnScroll() {
    return this.dataset.hideOnScroll === 'true';
  }

  get #distance() {
    const raw = parseFloat(getComputedStyle(this).getPropertyValue('--sh-scroll-distance'));
    return Number.isFinite(raw) && raw > 0 ? raw : 140;
  }

  #measure = () => {
    const height = this.offsetHeight;
    document.body.style.setProperty('--header-height', `${height}px`);
    document.body.style.setProperty('--header-group-height', `${this.#overlay ? 0 : height}px`);
    document.body.style.setProperty('--transparent-header-offset-boolean', '0');
  };

  #onBreakpoint = () => {
    if (!this.#desktop.matches) this.#closePanel();
  };

  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  };

  #update() {
    const y = Math.max(0, window.scrollY);
    const delta = y - this.#lastY;

    if (this.#overlay) {
      const progress = Math.min(1, y / this.#distance);
      this.style.setProperty('--sh-progress', progress.toFixed(4));
      this.dataset.scrollState = progress <= 0.05 ? 'top' : progress <= 0.6 ? 'blur' : 'solid';
    }

    if (this.#hideOnScroll) {
      if (y < this.#revealAbove) {
        this.#setHidden(false);
      } else if (delta > this.#directionThreshold) {
        this.#setHidden(true);
      } else if (delta < -this.#directionThreshold) {
        this.#setHidden(false);
      }
    }

    // Only move the reference point once it has travelled far enough to mean something, so a
    // slow scroll accumulates into a direction instead of reading as noise every frame.
    if (Math.abs(delta) > this.#directionThreshold) this.#lastY = y;
  }

  /** @param {boolean} hidden */
  #setHidden(hidden) {
    if ((this.dataset.hidden === 'true') === hidden) return;
    this.dataset.hidden = String(hidden);
    if (hidden) this.#closePanel();
  }

  /** @param {Event} event */
  #onPointerEnter = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const link = target.closest('.site-header__nav-link');
    if (link instanceof HTMLAnchorElement) this.#openFor(link);
  };

  /** @param {FocusEvent} event */
  #onFocusIn = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const link = target.closest('.site-header__nav-link');

    if (link instanceof HTMLAnchorElement) {
      this.#openFor(link);
    } else if (!target.closest('.site-header__panel')) {
      // Focus moved to the cart or the language switcher: the panel is no longer the subject.
      this.#closePanel();
    }
  };

  /** @param {Event} event */
  #onLeave = (event) => {
    // focusout fires with relatedTarget still inside while tabbing between panel links.
    if (event instanceof FocusEvent && event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) {
      return;
    }
    this.#closePanel();
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape' || !this.#openPanel) return;
    const link = this.#linkFor(this.#openPanel);
    this.#closePanel();
    link?.focus();
  };

  /** @param {HTMLAnchorElement} link */
  #openFor(link) {
    const panelId = link.dataset.panel;

    if (!panelId || !this.#desktop.matches) {
      this.#closePanel();
      return;
    }
    if (this.#openPanel === panelId) return;

    this.#openPanel = panelId;

    for (const panel of this.refs.panels ?? []) {
      panel.hidden = panel.dataset.panel !== panelId;
    }
    for (const navLink of this.refs.navLinks ?? []) {
      if (navLink.dataset.panel) navLink.setAttribute('aria-expanded', String(navLink.dataset.panel === panelId));
    }
    if (this.refs.panelShell) this.refs.panelShell.dataset.open = 'true';
  }

  #closePanel() {
    if (!this.#openPanel) return;
    this.#openPanel = null;

    if (this.refs.panelShell) this.refs.panelShell.dataset.open = 'false';
    for (const navLink of this.refs.navLinks ?? []) {
      if (navLink.dataset.panel) navLink.setAttribute('aria-expanded', 'false');
    }
    // Left in the DOM until the collapse has run, otherwise the panel disappears before it closes.
    setTimeout(() => {
      if (this.#openPanel) return;
      for (const panel of this.refs.panels ?? []) panel.hidden = true;
    }, 500);
  }

  /** @param {string} panelId */
  #linkFor(panelId) {
    return (this.refs.navLinks ?? []).find((link) => link.dataset.panel === panelId);
  }
}

if (!customElements.get('site-header-component')) {
  customElements.define('site-header-component', SiteHeaderComponent);
}
